import React from 'react';
import * as Yup from 'yup';
import {
  ColumnSet, MutationAction, QueryAction, Resource,
  StringFieldMinMax,
  RefField,
} from 'ants/resource';
import { arrayAsObject, objectAsArray } from 'lib/utils';
import { CmsPageImageView } from './ResourceImage';
import {
  CmsPageSearchQuery,
  CmsPageReadQuery,
  CmsPageCreateMutation,
  CmsPageUpdateMutation,
  CmsPageNameQuery,
} from './query';

const LocalFields = ['localFieldBodyType'];
const ResourceSourceTypes = ['Resource', 'Image'];
const RawSourceTypes = ['Raw', 'ClientView', 'Outlet'];

function contentAsObject(content) {
  const newContent = content.map((item) => {
    const newItem = { ...item };
    if (ResourceSourceTypes.includes(newItem.source_type)) {
      if (newItem.resource_key) {
        newItem.resource_key = { hashkey: newItem.resource_key };
      }
    } else {
      newItem.resource_key = null;
    }

    if (newItem.structure && newItem.structure.hashkey) {
      newItem.structure = { hashkey: newItem.structure.hashkey };
    }

    newItem.children = newItem.children ? contentAsObject(newItem.children) : {};
    return newItem;
  });
  return arrayAsObject(newContent);
}

function contentAsArray(content) {
  let newContent = objectAsArray(content);
  newContent = newContent.map((item) => {
    const newItem = { ...item };

    if (newItem.source_type !== 'Structure') {
      newItem.structure = null;
      newItem.structure_content = null;
      newItem.structure_data = null;
    } else {
      newItem.structure = newItem.structure ? { _hashkey: newItem.structure.hashkey } : null;
      newItem.structure_content = newItem.structure_content
        ? { _hashkey: newItem.structure_content.hashkey }
        : null;
      // newItem.structure_data = objectAsArray(newItem.structure_data);
    }

    if (newItem.source_type === 'Image') {
      newItem.resource_key = newItem.resource_key
        ? newItem.resource_key.hashkey
        : null;
    }

    if (newItem.source_type === 'Resource') {
      newItem.resource_key = newItem.resource_key
        ? newItem.resource_key.hashkey
        : null;
    }

    if (!ResourceSourceTypes.includes(newItem.source_type)) {
      newItem.resource_key = null;
    }

    if (!RawSourceTypes.includes(newItem.source_type)) {
      newItem.raw = '';
    }

    if (newItem.source_type !== 'Div') {
      newItem.children = {};
    }

    newItem.children = newItem.children ? contentAsArray(newItem.children) : {};
    return newItem;
  });
  return newContent;
}

function parseNestedData(obj) {
  Object.entries(obj).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (value?.data) {
        // eslint-disable-next-line no-param-reassign
        obj[key] = { ...value.data };
        parseNestedData(obj[key]);
      } else {
        parseNestedData(value);
      }
    }
  });
}

export const CmsPageResource = Resource({
  resourceId: 'page',
  app: 'cms',
  name: 'page Page',
  keyVal: 'CmsPage',
  topicName: 'CmsPage',
  storeId: 'CmsPage',
  sportScope: false,
  columnSets: [
    ColumnSet({
      name: 'Page',
      shape: Yup.object().shape({
        name: StringFieldMinMax(2, 120),
        slug: StringFieldMinMax(1, 120),
        parent: RefField(),
        base_page: RefField(),
      }),
      referenceFields: ['parent', 'base_page'],
    }),
    ColumnSet({
      name: 'Body',
      shape: Yup.object().shape({
        by_structure: RefField(),
        by_structure_content: RefField(),
      }),
      referenceFields: ['by_structure', 'by_structure_content'],
    }),
    // ColumnSet({
    //   name: 'Desc',
    //   shape: Yup.object().shape({
    //     desc: Yup.string().max(1024, 'Too big').nullable().default(''),
    //     image: Yup.string(),
    //   }),
    // }),
  ],
  listPrimaryActions: [
    { action: 'update' },
  ],
  imageRender: (item) => (<CmsPageImageView item={item} />),
  queries: {
    read: QueryAction({
      query: CmsPageReadQuery,
      resourceNamePath: 'item.page.name',
      resourcePath: 'item.resource.hashkey',
    }),
    list: QueryAction({ query: CmsPageSearchQuery, responsePath: 'cms_page_search' }),
    search: QueryAction({
      query: CmsPageSearchQuery,
      resourcePath: 'resource.hashkey',
      resourceNamePath: 'page.name',
    }),
    name: QueryAction({ query: CmsPageNameQuery, resourceNamePath: 'cms_page_name.name' }),
  },
  mutations: {
    update: MutationAction({
      mutation: CmsPageUpdateMutation,
      cs: ['page', 'body'],
      prepareForEdit(self, resource, data) {
        const resp = self.defaultPrepareForEdit(self, resource, data);
        if (!resp.body.content) {
          resp.body.content = [];
        }
        resp.body.content = contentAsObject(
          resp.body.content,
        );
        return resp;
      },
      prepareForSave(self, resource, data) {
        const resp = self.defaultPrepareForSave(self, resource, data);

        LocalFields.forEach((field) => {
          resp.body[field] = undefined;
        });
        resp.body.content = contentAsArray(resp.body.content);
        resp.body.body_scripts = objectAsArray(resp.body.body_scripts);
        resp.body.footer_scripts = objectAsArray(resp.body.footer_scripts);
        if (resp?.body?.body_content_type === 'Structure' && resp?.body?.by_structure_data) {
          parseNestedData(resp.body.by_structure_data);
        }
        return resp;
      },
    }),
    create: MutationAction({ mutation: CmsPageCreateMutation }),
  },
});
